import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  Store, Eye, HeartHandshake, Truck, Shield, CheckCircle2, ArrowRight,
  Clock, Building2, Phone, FileCheck, Send, Sparkles, Users, TrendingUp,
  Lock, ChevronRight
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Animated counter hook
// ---------------------------------------------------------------------------
const useAnimatedCount = (target, duration = 1200) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
};

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------
const Section = ({ children, className = '' }) => (
  <section className={`mb-16 ${className}`}>{children}</section>
);

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-10">
    <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">{children}</h2>
    {sub && <p className="text-secondary max-w-xl mx-auto">{sub}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// PILLARS
// ---------------------------------------------------------------------------
const PILLARS = [
  {
    icon: Eye,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    title: 'Visibilite pro',
    desc: 'Publiez vos vehicules sur un reseau de professionnels verifies. Touchez des acheteurs qualifies, prets a conclure.',
  },
  {
    icon: HeartHandshake,
    color: 'text-accent-purple',
    bg: 'bg-accent-purple-bg',
    border: 'border-accent-purple/20',
    title: 'Negociation integree',
    desc: 'Offres, contre-offres et suivi en temps reel. Tout se passe dans Carmin, sans perdre le fil.',
  },
  {
    icon: Truck,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    title: 'Logistique simplifiee',
    desc: 'Planifiez des rendez-vous, gerez les visites et concluez vos ventes sans quitter la plateforme.',
  },
];

// ---------------------------------------------------------------------------
// STEPS
// ---------------------------------------------------------------------------
const STEPS = [
  { num: 1, title: 'Demandez l\'acces', desc: 'Remplissez le formulaire avec vos informations professionnelles.' },
  { num: 2, title: 'Verification', desc: 'Notre equipe verifie votre identite et votre activite.' },
  { num: 3, title: 'Activation', desc: 'Votre compte est active et vous accedez a toutes les fonctionnalites.' },
  { num: 4, title: 'Publiez et vendez', desc: 'Listez vos vehicules et commencez a recevoir des offres.' },
];

// ---------------------------------------------------------------------------
// Blurred listing card placeholder
// ---------------------------------------------------------------------------
const BlurredCard = ({ index }) => {
  const makes = ['BMW', 'Audi', 'Mercedes', 'Volkswagen', 'Peugeot', 'Renault'];
  const models = ['Serie 3', 'A4', 'Classe C', 'Golf', '308', 'Megane'];
  const prices = ['24 900', '31 500', '42 000', '18 750', '22 300', '35 800'];
  const i = index % makes.length;

  return (
    <div className="rounded-xl border border-dark-border bg-dark-card overflow-hidden select-none">
      {/* Fake image */}
      <div className="h-36 bg-gradient-to-br from-dark-hover to-dark-surface relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <Store className="h-10 w-10 text-dark-border" />
        </div>
      </div>
      {/* Fake content */}
      <div className="p-3 space-y-2">
        <div className="text-sm font-semibold text-primary">{makes[i]} {models[i]}</div>
        <div className="flex items-center gap-2 text-xs text-muted">
          <span>2023</span>
          <span>45 000 km</span>
          <span>Diesel</span>
        </div>
        <div className="text-lg font-bold text-accent-purple">{prices[i]} &euro;</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MarketplaceConversion component
// ---------------------------------------------------------------------------
const MarketplaceConversion = () => {
  const { user, refreshUser } = useAuth();

  // Form state
  const [country, setCountry] = useState('france'); // 'france' or 'belgique'
  const [siret, setSiret] = useState('');
  const [tvaNumber, setTvaNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Listing count for social proof
  const [listingCount, setListingCount] = useState(0);
  const animatedCount = useAnimatedCount(listingCount);

  // Pre-fill from user settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get('/settings');
        const settings = res.data;
        if (settings.company_phone) setPhone(settings.company_phone);
        if (settings.siret) setSiret(settings.siret);
        if (settings.tva_number) {
          setTvaNumber(settings.tva_number);
          setCountry('belgique');
        }
      } catch {
        // Settings not yet created — that's fine
      }
    };
    loadSettings();
  }, []);

  // Fetch listing count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await api.get('/marketplace/listing-count');
        setListingCount(res.data.count || 0);
      } catch {
        setListingCount(0);
      }
    };
    fetchCount();
  }, []);

  const isPending = user?.marketplace_status === 'pending' || submitted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (country === 'france' && !siret.trim()) {
      setError('Le numero SIRET est requis.');
      return;
    }
    if (country === 'belgique' && !tvaNumber.trim()) {
      setError('Le numero de TVA est requis.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/marketplace/access-request', {
        siret: country === 'france' ? siret.trim() : null,
        tva_number: country === 'belgique' ? tvaNumber.trim() : null,
        company_phone: phone.trim() || null,
      });
      setSubmitted(true);
      if (refreshUser) await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi de la demande.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto pb-16">
      {/* ================================================================ */}
      {/* PENDING BANNER                                                    */}
      {/* ================================================================ */}
      {isPending && (
        <div className="mb-8 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Clock className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-amber-300 mb-1">Demande en cours de verification</h3>
            <p className="text-sm text-secondary">
              Votre demande d'acces a la Marketplace a ete envoyee. Notre equipe la verifie dans les plus brefs delais.
              Vous recevrez une notification des que votre compte sera active.
            </p>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* HERO                                                              */}
      {/* ================================================================ */}
      <Section>
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-accent-purple-bg via-dark-card to-dark-surface border border-accent-purple/20 p-8 md:p-12">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent-purple/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-purple/10 border border-accent-purple/20 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-accent-purple" />
              <span className="text-xs font-medium text-accent-purple-light">Marketplace Pro B2B</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Vendez vos vehicules aux professionnels verifies
            </h1>

            <p className="text-lg text-secondary mb-8 leading-relaxed">
              Rejoignez un reseau de concessionnaires de confiance. Publiez vos annonces,
              recevez des offres et concluez vos ventes — le tout depuis Carmin.
            </p>

            {!isPending && (
              <a
                href="#access-form"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent-purple hover:bg-accent-purple-dark text-white font-semibold transition-colors"
              >
                Demander l'acces
                <ArrowRight className="h-4 w-4" />
              </a>
            )}

            {/* Social proof */}
            {listingCount > 0 && (
              <div className="mt-8 flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-400" />
                  <span className="text-sm text-secondary">
                    <span className="font-semibold text-primary">{animatedCount}</span> annonces actives
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-400" />
                  <span className="text-sm text-secondary">Pros verifies uniquement</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* ================================================================ */}
      {/* 3 PILLARS                                                        */}
      {/* ================================================================ */}
      <Section>
        <SectionTitle sub="Tout ce dont vous avez besoin pour vendre entre professionnels.">
          Pourquoi la Marketplace Carmin ?
        </SectionTitle>

        <div className="grid md:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className={`rounded-xl border ${p.border} ${p.bg} p-6 transition-all hover:scale-[1.02] hover:shadow-lg`}
            >
              <div className={`w-10 h-10 rounded-lg ${p.bg} flex items-center justify-center mb-4`}>
                <p.icon className={`h-5 w-5 ${p.color}`} />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">{p.title}</h3>
              <p className="text-sm text-secondary leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ================================================================ */}
      {/* HOW IT WORKS                                                     */}
      {/* ================================================================ */}
      <Section>
        <SectionTitle sub="De la demande a la premiere vente, en quelques etapes.">
          Comment ca marche
        </SectionTitle>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-dark-border" style={{ left: '12.5%', right: '12.5%' }} />

          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map((s, idx) => (
              <div key={s.num} className="relative text-center">
                {/* Number circle */}
                <div className="relative z-10 w-16 h-16 rounded-full bg-dark-card border-2 border-accent-purple/40 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-accent-purple">{s.num}</span>
                </div>
                <h4 className="text-sm font-semibold text-primary mb-1.5">{s.title}</h4>
                <p className="text-xs text-muted leading-relaxed">{s.desc}</p>
                {/* Arrow between steps (mobile) */}
                {idx < STEPS.length - 1 && (
                  <ChevronRight className="md:hidden h-5 w-5 text-dark-border mx-auto mt-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ================================================================ */}
      {/* BLURRED PREVIEW                                                  */}
      {/* ================================================================ */}
      <Section>
        <SectionTitle sub="Un apercu de ce qui vous attend sur la Marketplace.">
          Des vehicules de pros, pour les pros
        </SectionTitle>

        <div className="relative">
          {/* Blurred cards grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 blur-[6px] select-none pointer-events-none">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <BlurredCard key={i} index={i} />
            ))}
          </div>

          {/* Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-dark-bg/40">
            <div className="text-center bg-dark-card/95 backdrop-blur-sm rounded-2xl border border-accent-purple/30 p-8 shadow-2xl max-w-sm mx-4">
              <div className="w-14 h-14 rounded-full bg-accent-purple/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="h-7 w-7 text-accent-purple" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">
                {listingCount > 0
                  ? `${listingCount} annonces disponibles`
                  : 'Annonces reservees aux pros'}
              </h3>
              <p className="text-sm text-secondary mb-5">
                Debloquez l'acces pour parcourir, publier et negocier.
              </p>
              {!isPending && (
                <a
                  href="#access-form"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-accent-purple hover:bg-accent-purple-dark text-white text-sm font-medium transition-colors"
                >
                  Demander l'acces
                  <ArrowRight className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* ================================================================ */}
      {/* WHY VERIFICATION                                                 */}
      {/* ================================================================ */}
      <Section>
        <div className="rounded-xl border border-dark-border bg-dark-card p-8 md:p-10">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Shield className="h-7 w-7 text-emerald-400" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-primary mb-3">Pourquoi la verification ?</h3>
              <p className="text-secondary leading-relaxed mb-5">
                La verification garantit un environnement de confiance. Chaque professionnel est
                identifie et valide, ce qui vous assure de traiter uniquement avec des acteurs serieux.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  'Identite professionnelle verifiee',
                  'Transactions securisees',
                  'Reseau de confiance exclusif',
                  'Protection contre les arnaques',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-sm text-secondary">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ================================================================ */}
      {/* ACCESS REQUEST FORM                                              */}
      {/* ================================================================ */}
      {!isPending && (
        <Section>
          <div id="access-form" className="scroll-mt-8">
            <SectionTitle sub="Remplissez vos informations professionnelles pour acceder a la Marketplace.">
              Demander l'acces
            </SectionTitle>

            <div className="max-w-lg mx-auto">
              <form onSubmit={handleSubmit} className="rounded-xl border border-dark-border bg-dark-card p-6 md:p-8 space-y-5">
                {/* Company name (read-only from user) */}
                <div>
                  <label className="block text-sm font-medium text-label mb-1.5">
                    <Building2 className="inline h-4 w-4 mr-1.5 text-muted" />
                    Nom de l'entreprise
                  </label>
                  <input
                    type="text"
                    value={user?.company || user?.name || ''}
                    disabled
                    className="w-full px-3 py-2.5 rounded-lg bg-dark-hover border border-dark-border text-muted text-sm cursor-not-allowed"
                  />
                  <p className="text-xs text-muted mt-1">Modifiable dans les parametres de votre compte.</p>
                </div>

                {/* Country selector */}
                <div>
                  <label className="block text-sm font-medium text-label mb-1.5">Pays</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setCountry('france')}
                      className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        country === 'france'
                          ? 'border-accent-purple bg-accent-purple/10 text-accent-purple-light'
                          : 'border-dark-border bg-dark-surface text-secondary hover:border-dark-hover'
                      }`}
                    >
                      France
                    </button>
                    <button
                      type="button"
                      onClick={() => setCountry('belgique')}
                      className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        country === 'belgique'
                          ? 'border-accent-purple bg-accent-purple/10 text-accent-purple-light'
                          : 'border-dark-border bg-dark-surface text-secondary hover:border-dark-hover'
                      }`}
                    >
                      Belgique
                    </button>
                  </div>
                </div>

                {/* SIRET or TVA */}
                {country === 'france' ? (
                  <div>
                    <label className="block text-sm font-medium text-label mb-1.5">
                      <FileCheck className="inline h-4 w-4 mr-1.5 text-muted" />
                      Numero SIRET
                    </label>
                    <input
                      type="text"
                      value={siret}
                      onChange={(e) => setSiret(e.target.value)}
                      placeholder="123 456 789 00012"
                      className="w-full px-3 py-2.5 rounded-lg bg-dark-surface border border-dark-border text-primary text-sm placeholder-muted focus:outline-none focus:border-accent-purple transition-colors"
                    />
                    <p className="text-xs text-muted mt-1">14 chiffres, disponible sur votre Kbis.</p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-label mb-1.5">
                      <FileCheck className="inline h-4 w-4 mr-1.5 text-muted" />
                      Numero de TVA
                    </label>
                    <input
                      type="text"
                      value={tvaNumber}
                      onChange={(e) => setTvaNumber(e.target.value)}
                      placeholder="BE 0123.456.789"
                      className="w-full px-3 py-2.5 rounded-lg bg-dark-surface border border-dark-border text-primary text-sm placeholder-muted focus:outline-none focus:border-accent-purple transition-colors"
                    />
                    <p className="text-xs text-muted mt-1">Format: BE suivi de 10 chiffres.</p>
                  </div>
                )}

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-label mb-1.5">
                    <Phone className="inline h-4 w-4 mr-1.5 text-muted" />
                    Telephone professionnel
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={country === 'france' ? '+33 6 12 34 56 78' : '+32 4 12 34 56 78'}
                    className="w-full px-3 py-2.5 rounded-lg bg-dark-surface border border-dark-border text-primary text-sm placeholder-muted focus:outline-none focus:border-accent-purple transition-colors"
                  />
                </div>

                {/* Error message */}
                {error && (
                  <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-accent-purple hover:bg-accent-purple-dark text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Envoyer ma demande
                    </>
                  )}
                </button>

                <p className="text-xs text-muted text-center">
                  En soumettant ce formulaire, vous acceptez que vos informations soient verifiees par notre equipe.
                </p>
              </form>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
};

export default MarketplaceConversion;
